/**
 * API URLs Enum
 * Centralized API endpoint definitions organized by module
 * Base URL is configured in API_CONFIG
 */

/**
 * Authentication Module URLs
 */
export const AuthUrls = {
  LOGIN: '/api/v1/auth/hcms/login',
  REGISTER: '/api/v1/auth/register',
  LOGOUT: '/api/v1/auth/hcms/logout',
  REFRESH_TOKEN: '/api/health/v1/auth/hcms/refresh',
  FORGOT_PASSWORD: '/api/health/v1/auth/forgot-password',
  RESET_PASSWORD: '/api/health/v1/auth/reset-password',
  VERIFY_EMAIL: '/api/v1/auth/verify-email',
  CHANGE_PASSWORD: '/api/health/v1/auth/change-password',
  All_ASSETS_STATUS_API: '/api/health/v1/admin/commonmaster?name=ALL',
} as const;

/**
 * Admin Module URLs
 */
export const AdminUrls = {
  BASE: '/api/v1/admin',
  DASHBOARD: '/api/v1/admin/dashboard',
  USERS: '/api/v1/admin/users',
  USER_BY_ID: (id: string) => `/api/v1/admin/users/${id}`,
  SETTINGS: '/api/v1/admin/settings',
  AUDIT_LOGS: '/api/v1/admin/audit-logs',
  TENANTS: '/api/v1/admin/tenants',
  TENANT_BY_ID: (id: string) => `/api/v1/admin/tenants/${id}`
} as const;

/**
 * Tenant Module URLs
 */
export const TenantUrls = {
  BASE: '/api/tenant',
  STEP1: '/api/health/v1/tenant/step1',
  STEP2: '/api/health/v1/tenant/step2',
  STEP3: '/api/health/v1/tenant/step3',
  STEP4: '/api/health/v1/tenant/step4',
  SUBMIT: '/api/health/v1/tenant/submit',
  LIST: '/api/v1/tenant',
  BY_ID: (id: string) => `/api/health/v1/tenant/${id}`,
  UPDATE: (id: string) => `/api/health/v1/tenant/${id}`,
  DELETE: (id: string) => `/api/health/v1/tenant/${id}`,
  GET_TENANT_LIST_BY_TYPE:(isDraft: boolean) =>  `/api/health/v1/tenant/gettenants?isDraft=${isDraft}`,
  DRAFTS: '/api/health/v1/tenant/drafts',
  SAVE_DRAFT: '/api/health/v1/tenant/drafts',
  DRAFT_BY_ID: (id: string) => `/api/health/v1/tenant/drafts/${id}`,
  DELETE_DRAFT: (id: string) => `/api/health/v1/tenant/drafts/${id}`,
  SUBSCRIPTION_PLANS: '/api/health/v1/admin/subscription-plans',
  APPROVE_TENANT: (id: string) => `/api/health/v1/tenant/${id}/approve`,
  SEND_PAYMENT_EMAIL: (id: string) => `/api/health/v1/tenant/${id}/send-payment-email`,
  SAVE_KYC_DOCUMENTS: (id: string) => `/api/health/v1/tenant/${id}/kyc/documents`,
  GET_KYC_DOCUMENTS: (id: string) => `/api/health/v1/tenant/${id}/kyc/documents`,
  APPROVE_KYC: (id: string) => `/api/health/v1/tenant/${id}/kyc/approve`,
  ACTIVATE_TENANT: (id: string) => `/api/health/v1/tenant/${id}/activate`,
  // SUBSCRIPTION_BY_ID: (id: string) => `/api/v1/admin/subscription-plans/${id}`
  
} as const;

/**
 * Patient Module URLs
 */
export const PatientUrls = {
  BASE: '/api/health/v1/patients',
  LIST: '/api/health/v1/patients',
  BY_ID: (id: string) => `/api/health/v1/patients/${id}`,
  CREATE: '/api/health/v1/patients',
  UPDATE: (id: string) => `/api/health/v1/patients/${id}`,
  DELETE: (id: string) => `/api/health/v1/patients/${id}`,
  SEARCH: '/api/health/v1/patients/search',
  MEDICAL_HISTORY: (id: string) => `/api/health/v1/patients/${id}/medical-history`,
  APPOINTMENTS: (id: string) => `/api/health/v1/patients/${id}/appointments`,
  PRESCRIPTIONS: (id: string) => `/api/health/v1/patients/${id}/prescriptions`,
  BILLS: (id: string) => `/api/health/v1/patients/${id}/bills`
} as const;

/**
 * Appointment Module URLs
 */
export const AppointmentUrls = {
  BASE: '/api/health/v1/appointments',
  LIST: '/api/health/v1/appointments',
  BY_ID: (id: string) => `/api/health/v1/appointments/${id}`,
  CREATE: '/api/health/v1/appointments',
  UPDATE: (id: string) => `/api/health/v1/appointments/${id}`,
  DELETE: (id: string) => `/api/health/v1/appointments/${id}`,
  CANCEL: (id: string) => `/api/health/v1/appointments/${id}/cancel`,
  RESCHEDULE: (id: string) => `/api/health/v1/appointments/${id}/reschedule`,
  BY_PATIENT: (patientId: string) => `/api/health/v1/appointments/patient/${patientId}`,
  BY_DOCTOR: (doctorId: string) => `/api/health/v1/appointments/doctor/${doctorId}`,
  BY_DATE: (date: string) => `/api/health/v1/appointments/date/${date}`,
  UPCOMING: '/api/health/v1/appointments/upcoming',
  TODAY: '/api/health/v1/appointments/today'
} as const;

/**
 * Medical Records Module URLs
 */
export const MedicalRecordUrls = {
  BASE: '/api/health/v1/medical-records',
  LIST: '/api/health/v1/medical-records',
  BY_ID: (id: string) => `/api/health/v1/medical-records/${id}`,
  CREATE: '/api/health/v1/medical-records',
  UPDATE: (id: string) => `/api/health/v1/medical-records/${id}`,
  DELETE: (id: string) => `/api/health/v1/medical-records/${id}`,
  BY_PATIENT: (patientId: string) => `/api/health/v1/medical-records/patient/${patientId}`,
  BY_DOCTOR: (doctorId: string) => `/api/health/v1/medical-records/doctor/${doctorId}`,
  SEARCH: '/api/health/v1/medical-records/search'
} as const;

/**
 * Prescription Module URLs
 */
export const PrescriptionUrls = {
  BASE: '/api/health/v1/prescriptions',
  LIST: '/api/health/v1/prescriptions',
  BY_ID: (id: string) => `/api/health/v1/prescriptions/${id}`,
  CREATE: '/api/health/v1/prescriptions',
  UPDATE: (id: string) => `/api/health/v1/prescriptions/${id}`,
  DELETE: (id: string) => `/api/health/v1/prescriptions/${id}`,
  BY_PATIENT: (patientId: string) => `/api/health/v1/prescriptions/patient/${patientId}`,
  BY_DOCTOR: (doctorId: string) => `/api/health/v1/prescriptions/doctor/${doctorId}`,
  ACTIVE: '/api/health/v1/prescriptions/active',
  REFILL: (id: string) => `/api/health/v1/prescriptions/${id}/refill`
} as const;

/**
 * Billing Module URLs
 */
export const BillingUrls = {
  BASE: '/api/health/v1/billing',
  BILLS: '/api/health/v1/billing/bills',
  BILL_BY_ID: (id: string) => `/api/health/v1/billing/bills/${id}`,
  CREATE_BILL: '/api/health/v1/billing/bills',
  UPDATE_BILL: (id: string) => `/api/health/v1/billing/bills/${id}`,
  DELETE_BILL: (id: string) => `/api/health/v1/billing/bills/${id}`,
  BY_PATIENT: (patientId: string) => `/api/health/v1/billing/bills/patient/${patientId}`,
  PAYMENT: '/api/health/v1/billing/payments',
  PAYMENT_BY_ID: (id: string) => `/api/health/v1/billing/payments/${id}`,
  RECEIPT: (id: string) => `/api/health/v1/billing/bills/${id}/receipt`,
  INVOICE: (id: string) => `/api/health/v1/billing/bills/${id}/invoice`,
  PENDING: '/api/health/v1/billing/bills/pending',
  PAID: '/api/health/v1/billing/bills/paid'
} as const;

/**
 * User Module URLs
 */
export const UserUrls = {
  BASE: '/api/health/v1/users',
  LIST: '/api/health/v1/users',
  BY_ID: (id: string) => `/api/health/v1/users/${id}`,
  CREATE: '/api/health/v1/users',
  UPDATE: (id: string) => `/api/health/v1/users/${id}`,
  DELETE: (id: string) => `/api/health/v1/users/${id}`,
  PROFILE: '/api/health/v1/users/profile',
  UPDATE_PROFILE: '/api/health/v1/users/profile',
  CHANGE_PASSWORD: '/api/health/v1/users/change-password',
  BY_ROLE: (role: string) => `/api/health/v1/users/role/${role}`,
  BY_DEPARTMENT: (department: string) => `/api/health/v1/users/department/${department}`
} as const;

/**
 * Consultation Module URLs
 */
export const ConsultationUrls = {
  BASE: '/api/health/v1/consultations',
  LIST: '/api/health/v1/consultations',
  BY_ID: (id: string) => `/api/health/v1/consultations/${id}`,
  CREATE: '/api/health/v1/consultations',
  UPDATE: (id: string) => `/api/health/v1/consultations/${id}`,
  DELETE: (id: string) => `/api/health/v1/consultations/${id}`,
  BY_PATIENT: (patientId: string) => `/api/health/v1/consultations/patient/${patientId}`,
  BY_DOCTOR: (doctorId: string) => `/api/health/v1/consultations/doctor/${doctorId}`
} as const;

/**
 * Configuration and Health Module URLs
 */
export const ConfigUrls = {
  BASE: '/api/auth/health',
  INITIAL_DATA: '/api/v1/auth/health/initial-data',
  HEALTH_CHECK: '/api/auth/health',
  CONFIG: '/api/v1/config',
  SETTINGS: '/api/v1/config/settings'
} as const;

/**
 * Analytics Module URLs
 */
export const AnalyticsUrls = {
  BASE: '/api/health/v1/analytics',
  DASHBOARD: '/api/health/v1/analytics/dashboard',
  STATISTICS: '/api/health/v1/analytics/statistics',
  REPORTS: '/api/health/v1/analytics/reports',
  EXPORT: '/api/health/v1/analytics/export'
} as const;

/**
 * Messages/Notifications Module URLs
 */
export const MessageUrls = {
  BASE: '/api/health/v1/messages',
  LIST: '/api/health/v1/messages',
  BY_ID: (id: string) => `/api/health/v1/messages/${id}`,
  CREATE: '/api/health/v1/messages',
  UPDATE: (id: string) => `/api/health/v1/messages/${id}`,
  DELETE: (id: string) => `/api/health/v1/messages/${id}`,
  UNREAD: '/api/health/v1/messages/unread',
  MARK_READ: (id: string) => `/api/health/v1/messages/${id}/read`,
  NOTIFICATIONS: '/api/v1/notifications'
} as const;

/**
 * Helper function to build full URL with base URL
 * This can be used when you need the full URL including the base URL from API_CONFIG
 */
export class ApiUrlBuilder {
  /**
   * Build full URL with base URL
   * @param endpoint - API endpoint (e.g., AuthUrls.LOGIN or AuthUrls.USER_BY_ID('123'))
   * @param baseUrl - Base URL from API_CONFIG.getBaseUrl()
   * @returns Full URL
   */
  static buildFullUrl(endpoint: string, baseUrl: string): string {
    // Remove leading slash from endpoint if baseUrl ends with slash or vice versa
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${cleanBaseUrl}${cleanEndpoint}`;
  }
}
